import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { InputGroup, Form, Button } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import ContactUsModal from "../components/ContactUsModal";

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [currentIconSrc, setCurrentIconSrc] = useState("frame1.svg");
  const [bgColor, setBgColor] = useState("#ff9800");
  const [animate, setAnimate] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [currency, setCurrency] = useState("INR");
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const publicUrl = process.env.PUBLIC_URL || "";
  const revenueSlides = [
    { symbol: "$", value: "2,845.00", label: "USD" },
    { symbol: "₹", value: "1,99,150", label: "INR" },
  ];
  const businessRevenueSlides = [
    { symbol: "$", value: "42,500", label: "USD" },
    { symbol: "₹", value: "29,75,000", label: "INR" },
  ];
  const [slideIndex, setSlideIndex] = useState(0);
  const [businessSlideIndex, setbusinessSlideIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % revenueSlides.length);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setbusinessSlideIndex(
        (prev) => (prev + 1) % businessRevenueSlides.length
      );
    }, 2500);

    return () => clearInterval(interval);
  }, []);
  // Fetch categories and subcategories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const categoriesResponse = await axios.get(
          `${API_BASE_URL}/categories`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        const categoriesData = categoriesResponse.data;

        const categoriesWithSubcategories = await Promise.all(
          categoriesData.map(async (category) => {
            try {
              const subcategoriesResponse = await axios.get(
                `${API_BASE_URL}/categories/${category.id}/subcategories`,
                {
                  headers: { "Content-Type": "application/json" },
                }
              );
              return {
                ...category,
                icon: getCategoryIcon(category.name),
                subcategories: subcategoriesResponse.data || [],
              };
            } catch (err) {
              console.error(
                `Failed to fetch subcategories for category ${category.name}:`,
                err
              );
              return {
                ...category,
                icon: getCategoryIcon(category.name),
                subcategories: [],
              };
            }
          })
        );

        setCategories(categoriesWithSubcategories);
        setFilteredCategories(categoriesWithSubcategories);
        setCategoriesLoading(false);
      } catch (err) {
        console.error("Fetch categories error:", err);
        setCategoriesError(
          `Error fetching categories: ${err.message}. Please try again later.`
        );
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Debounced search function
  const handleSearch = useCallback(
    (searchValue) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      setSearchTimeout(
        setTimeout(() => {
          if (!searchValue.trim()) {
            setFilteredCategories(categories);
            return;
          }

          const lowerSearch = searchValue.toLowerCase().trim();
          const filtered = categories
            .map((category) => {
              const categoryMatches = category.name
                .toLowerCase()
                .includes(lowerSearch);

              // Filter subcategories only if category doesn't match
              const matchingSubcategories = category.subcategories.filter(
                (subcat) => subcat.name.toLowerCase().includes(lowerSearch)
              );

              if (categoryMatches) {
                return {
                  ...category,
                  subcategories: category.subcategories, // keep all subcategories
                };
              }

              if (matchingSubcategories.length > 0) {
                return {
                  ...category,
                  subcategories: matchingSubcategories, // only matched subcategories
                };
              }

              return null;
            })
            .filter(Boolean);

          setFilteredCategories(filtered);
        }, 300)
      );
    },
    [categories, searchTimeout]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredCategories(categories);
  };

  // Category icons
  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      "Professional & Business Services": "bi-briefcase-fill",
      "Beauty, Grooming & Wellness": "bi-scissors",
      "Healthcare & Medical": "bi-heart-pulse",
      "Home & Repair Services": "bi-tools",
      "Real Estate & Property": "bi-house-fill",
      "Automobile & Transport": "bi-car-front-fill",
      "Events & Lifestyle": "bi-camera-fill",
      "Education & Training": "bi-book-fill",
      "Pet & Animal Care": "bi-paw",
      "Travel & Leisure": "bi-airplane-fill",
      "Food & Beverages": "bi-cup-straw",
      "Laundry & Maintenance": "bi-bucket-fill",
      "Childcare & Elderly Services": "bi-person-heart",
      "Miscellaneous / Local Utility": "bi-gear-fill",
    };
    return iconMap[categoryName] || "bi-gear-fill";
  };
  const priceMap = {
    INR: {
      basic: "1,500",
      professional: "3,500",
      enterprise: "7,000",
    },
    USD: {
      basic: "25",
      professional: "50",
      enterprise: "100",
    },
  };
  // Typewriter effect
  useEffect(() => {
    const words = [
      "schedule",
      "organize",
      "plan",
      "book",
      "host",
      "arrange",
      "automate",
      "streamline",
    ];
    const typewriter = document.getElementById("typewriter");
    let wordIndex = 0;
    let charIndex = 0;
    let isDeleting = false;

    function typeEffect() {
      if (!typewriter) return;

      const currentWord = words[wordIndex];
      const fullLength = currentWord.length;

      if (!isDeleting) {
        typewriter.textContent = currentWord.substring(0, charIndex + 1);
        charIndex++;
        if (charIndex === fullLength) {
          setTimeout(() => {
            isDeleting = true;
            typeEffect();
          }, 3000);
          return;
        }
      } else {
        typewriter.textContent = currentWord.substring(0, charIndex - 1);
        charIndex--;
        if (charIndex === 0) {
          isDeleting = false;
          wordIndex = (wordIndex + 1) % words.length;
        }
      }

      const speed = isDeleting ? 100 : 150;
      setTimeout(typeEffect, speed);
    }

    typeEffect();
  }, []);

  // Intersection observer for feature cards
  useEffect(() => {
    const cards = document.querySelectorAll(".cardd");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const newColor = entry.target.dataset.color;
            const newIcon = entry.target.dataset.icon;

            setAnimate(false);
            setTimeout(() => {
              setBgColor(newColor);
              setCurrentIconSrc(newIcon);
              setAnimate(true);
            }, 400);
          }
        });
      },
      { threshold: 0.6 }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Cleanup search timeout
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const HeroSection = () => {
    return (
      <>
        <section className="hero-gradient py-5">
          <div className="hero-blob green"></div>
          <div className="hero-blob blue"></div>

          <div className="container position-relative">
            <div className="row align-items-center gy-5">
              {/* LEFT */}
              <div className="col-lg-6 text-center text-lg-start">
                <div className="hero-badge mb-4">
                  <span className="pulse-dot"></span>
                  The #1 Platform for Everyone
                </div>

                <h1 className="hero-title mb-4">
                  Booking &amp; Ops <br className="d-none d-lg-block" />
                  <span className="text-gradient">Simpler Together.</span>
                </h1>

                <p className="fs-5 text-muted mb-4">
                  Whether you're finding the perfect service or managing a
                  growing business, TickTickGo brings everything into one
                  seamless flow.
                </p>

                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start mb-4">
                  <a href="#services" className="btn btn-dark btn-pill">
                    Book a Service
                  </a>
                  <a
                    href="#business-solutions"
                    className="btn btn-outline-secondary btn-pill"
                  >
                    For Business
                  </a>
                </div>

                <div className="d-flex gap-4 justify-content-center justify-content-lg-start text-muted fw-semibold">
                  <div>✔ Free for users</div>
                  <div>✔ 14-day business trial</div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="col-lg-6 position-relative d-flex justify-content-center">
                <div className="floating-card floating-left d-none d-lg-block">
                  <strong className="text-success">Book Now</strong>
                </div>

                <div className="dashboard-wrap">
                  <img
                    src="dashboard.png"
                    className="img-fluid"
                    alt="Dashboard"
                  />

                  <div className="revenue-bar d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-uppercase text-muted fw-bold">
                        Today's Revenue
                      </small>
                      <div className="revenue-slider">
                        <div
                          key={slideIndex}
                          className="fs-4 fw-bold revenue-slide"
                        >
                          {revenueSlides[slideIndex].symbol}
                          {revenueSlides[slideIndex].value}
                          <small className="ms-2 text-muted">
                            {revenueSlides[slideIndex].label}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-circle bg-success bg-opacity-25 p-2 text-success">
                      ▲
                    </div>
                  </div>
                </div>

                <div className="floating-card floating-right d-none d-lg-block">
                  <small className="text-muted fw-bold text-uppercase">
                    Staff Active
                  </small>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INLINE STYLES (VALID IN REACT) */}
        <style>{`
        .hero-gradient {
          background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 55%);
          position: relative;
          overflow: hidden;
        }

        .hero-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
        }
        .hero-blob.green {
          width: 380px;
          height: 380px;
          background: rgba(34,197,94,0.15);
          top: -120px;
          right: -120px;
        }
        .hero-blob.blue {
          width: 300px;
          height: 300px;
          background: rgba(59,130,246,0.15);
          bottom: -120px;
          left: -120px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 10px rgba(0,0,0,.08);
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          color: #6b7280;
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #22c55e;
          position: relative;
        }
        .pulse-dot::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #22c55e;
          animation: ping 1.5s infinite;
          opacity: .6;
        }
        @keyframes ping {
          75%,100% { transform: scale(2); opacity: 0; }
        }

        .hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
        }

        .text-gradient {
          background: linear-gradient(to right, #22c55e, #4ade80);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .btn-pill {
          padding: 16px 32px;
          border-radius: 999px;
          font-weight: 700;
          font-size: 18px;
        }

        .floating-card {
          position: absolute;
          background: #fff;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 25px 60px rgba(0,0,0,.15);
          transition: transform .5s;
        }
        .floating-left {
          top: 40px;
          left: -30px;
          transform: rotate(-6deg);
        }
        .floating-right {
          bottom: 60px;
          right: -20px;
          transform: rotate(6deg);
        }
        .floating-card:hover {
          transform: rotate(0);
        }

        .dashboard-wrap {
          border-radius: 24px;
          overflow: hidden;
          border: 4px solid #fff;
          box-shadow: 0 40px 80px rgba(0,0,0,.25);
        }

        .revenue-bar {
          background: rgba(255,255,255,.9);
          backdrop-filter: blur(8px);
          padding: 16px;
          border-top: 1px solid #e5e7eb;
        }
        .revenue-slider {
          position: relative;
          height: 32px;
          overflow: hidden;
        }

        .revenue-slide {
          animation: slideFade 0.5s ease;
        }

        @keyframes slideFade {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      </>
    );
  };
  const BusinessSection = () => {
    return (
      <>
        <section
          id="business-solutions"
          className="py-5 position-relative overflow-hidden bg-white"
        >
          <div className="business-dots"></div>

          <div className="container position-relative">
            <div className="row align-items-center gy-5">
              {/* LEFT CONTENT */}
              <div className="col-lg-6">
                <div className="badge-business mb-4">For Business Owners</div>

                <h2 className="display-5 fw-extrabold mb-4">
                  Scale your operations <br />
                  <span className="text-success">without the chaos.</span>
                </h2>

                <p className="fs-5 text-muted mb-5">
                  TickTickGo provides a complete operating system tailored for
                  service businesses. From payroll to inventory, manage it all
                  in one powerful dashboard.
                </p>

                <div className="mb-5">
                  <div className="d-flex mb-4 feature-item">
                    <div className="feature-icon text-success">
                      <i className="bi bi-people-fill"></i>
                    </div>
                    <div className="ms-4">
                      <h5 className="fw-bold">Employee Management</h5>
                      <p className="text-muted mb-0">
                        Track attendance with geolocation, manage shifts
                        effortlessly, and approve leave requests instantly.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex mb-4 feature-item">
                    <div className="feature-icon text-success">
                      <i className="bi bi-box-seam"></i>
                    </div>
                    <div className="ms-4">
                      <h5 className="fw-bold">Smart Inventory</h5>
                      <p className="text-muted mb-0">
                        Real-time stock tracking with automated low-stock alerts
                        and vendor integration.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex feature-item">
                    <div className="feature-icon text-success">
                      <i className="bi bi-cash-stack"></i>
                    </div>
                    <div className="ms-4">
                      <h5 className="fw-bold">Payroll &amp; Invoicing</h5>
                      <p className="text-muted mb-0">
                        Automate salary calculations and generate professional
                        invoices.
                      </p>
                    </div>
                  </div>
                </div>

                <a
                  href="#"
                  className="btn btn-dark btn-lg rounded-pill px-5 shadow-lg"
                >
                  Explore Business Tools
                  <i className="bi bi-arrow-right ms-2"></i>
                </a>
              </div>

              {/* RIGHT DASHBOARD */}
              <div className="col-lg-6 position-relative">
                <div className="dashboard-glow"></div>

                <div className="dashboard-card">
                  <div className="dashboard-header">
                    <div className="d-flex gap-2">
                      <span className="dot red"></span>
                      <span className="dot yellow"></span>
                      <span className="dot green"></span>
                    </div>
                    <span className="dashboard-url">
                      ticktickgo.in/dashboard
                    </span>
                  </div>

                  <div className="p-4">
                    <div className="row g-3 mb-4">
                      <div className="col-6">
                        <div className="stat-card blue">
                          <i className="bi bi-bar-chart-fill"></i>
                          <p>Revenue</p>
                          <div className="revenue-slider">
                            <h4
                              key={businessSlideIndex}
                              className="revenue-slide"
                            >
                              {businessRevenueSlides[businessSlideIndex].symbol}
                              {businessRevenueSlides[businessSlideIndex].value}
                              <small className="ms-2 text-muted">
                                {
                                  businessRevenueSlides[businessSlideIndex]
                                    .label
                                }
                              </small>
                            </h4>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="stat-card purple">
                          <i className="bi bi-calendar-event"></i>
                          <p>Bookings</p>
                          <h4>1,240</h4>
                        </div>
                      </div>
                    </div>

                    <div className="activity-card mb-3">
                      <h6>Team Activity</h6>

                      <div className="activity-row">
                        <div className="avatar">JD</div>
                        <div>
                          <strong>John Doe</strong>
                          <small>Clocked in at 9:00 AM</small>
                        </div>
                        <span className="status-dot"></span>
                      </div>

                      <div className="activity-row">
                        <div className="avatar">AS</div>
                        <div>
                          <strong>Alice Smith</strong>
                          <small>Completed Inventory Check</small>
                        </div>
                        <small className="text-muted">2m ago</small>
                      </div>
                    </div>

                    <div className="plan-card">
                      <div>
                        <strong>Premium Plan</strong>
                        <small>Unlock all features</small>
                      </div>
                      <button className="btn btn-success btn-sm">
                        Upgrade
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* INLINE STYLES */}
        <style>{`
        .business-dots {
          position: absolute;
          inset: 0;
          opacity: .06;
          background-image: radial-gradient(#65a30d 1px, transparent 1px);
          background-size: 30px 30px;
          pointer-events: none;
        }

        .badge-business {
          display: inline-block;
          padding: 8px 14px;
          background: #111;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          border-radius: 8px;
          transform: rotate(-2deg);
          box-shadow: 0 10px 20px rgba(0,0,0,.15);
        }

        .feature-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: rgba(34,197,94,.15);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .dashboard-glow {
          position: absolute;
          inset: -16px;
          background: linear-gradient(90deg, #22c55e, #10b981);
          opacity: .25;
          filter: blur(24px);
          border-radius: 32px;
        }

        .dashboard-card {
          position: relative;
          background: #fff;
          border-radius: 24px;
          box-shadow: 0 40px 80px rgba(0,0,0,.25);
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .dashboard-header {
          padding: 12px 16px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .dot.red { background: #f87171; }
        .dot.yellow { background: #facc15; }
        .dot.green { background: #4ade80; }

        .dashboard-url {
          font-size: 11px;
          color: #9ca3af;
          font-family: monospace;
        }

        .stat-card {
          padding: 16px;
          border-radius: 16px;
          font-weight: 700;
        }
        .stat-card p {
          font-size: 11px;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .stat-card h4 {
          margin: 0;
        }
        .stat-card.blue {
          background: #eff6ff;
          color: #2563eb;
        }
        .stat-card.purple {
          background: #faf5ff;
          color: #7c3aed;
        }

        .activity-card {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 16px;
        }

        .activity-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 12px;
        }

        .avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background: #22c55e;
          border-radius: 50%;
        }

        .plan-card {
          background: #111;
          color: #fff;
          padding: 14px;
          border-radius: 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      `}</style>
      </>
    );
  };

  return (
    <>
      <link rel="stylesheet" href={`${publicUrl}/home.css`} />
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container">
          <a className="navbar-brand" href="#">
            <img src={`${publicUrl}/logo.png`} alt="TickTickGo" />
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon" />
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <a className="nav-link fw500 color1" href="#">
                  Home
                </a>
              </li>
              {/* <li className="nav-item">
                <a className="nav-link fw500 color1" href="#howitworks">
                  How it works
                </a>
              </li> */}
              <li className="nav-item">
                <a className="nav-link fw500 color1" href="#services">
                  Services
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link fw500 color1" href="#pricing">
                  Pricing
                </a>
              </li>
              <li className="nav-item">
                <ContactUsModal />
              </li>
            </ul>
            <div className="d-flex">
              <a href="/login" className="btn btn-light me-2">
                <i className="bi bi-box-arrow-in-right me-1" />
                Login
              </a>
              <a href="/signup" className="btn btn-success">
                <i className="bi bi-person-plus me-1" />
                Sign up
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <HeroSection />
      {/* Categories Section */}
      <section className="categories-section py-5 position-relative overflow-hidden" id="services">
        <div className="categories-bg-glow"></div>

        <div className="container position-relative">
          <div className="text-center mb-5">
            <h1 className="fw700 mb-3">Explore Our Services</h1>
            <p className="text-muted fs17 fw600 mb-4">
              Discover a wide range of professional services tailored to your
              needs.
            </p>

            <div style={{ maxWidth: "520px", margin: "0 auto" }}>
              <InputGroup className="search-pill shadow-sm">
                <Form.Control
                  type="text"
                  placeholder="Search categories or subcategories..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <i className="bi bi-x-circle"></i>
                  </Button>
                )}
              </InputGroup>

              {searchTerm && (
                <small className="text-muted d-block mt-2">
                  Found {filteredCategories.length}{" "}
                  {filteredCategories.length === 1 ? "match" : "matches"}
                </small>
              )}
            </div>
          </div>

          {/* STATES */}
          {categoriesLoading ? (
            <div className="text-center py-5">
              <i className="bi bi-arrow-repeat text-success spin me-2 fs-2"></i>
              Loading categories...
            </div>
          ) : categoriesError ? (
            <div className="text-center py-5 text-danger">
              <i className="bi bi-exclamation-triangle fs-2 me-2"></i>
              {categoriesError}
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-search fs-1 mb-3 d-block"></i>
              <h5>No results found</h5>
            </div>
          ) : (
            <div className="categories-scroll-wrapper">
              <div className="row g-4">
                {filteredCategories.map((category) => (
                  <div className="col-md-6 col-lg-4" key={category.id}>
                    <div className="category-card h-100">
                      <div className="category-header">
                        <div className="category-icon">
                          <i className={`bi ${category.icon}`}></i>
                        </div>
                        <h5 className="fw600 mb-0">{category.name}</h5>
                      </div>

                      <ul className="subcategory-list">
                        {category.subcategories.length > 0 ? (
                          category.subcategories.map((subcat) => (
                            <li key={subcat.id}>
                              <i className="bi bi-chevron-right"></i>
                              <Link
                                to={`/explore?categoryId=${
                                  category.id
                                }&categoryName=${encodeURIComponent(
                                  category.name
                                )}&subcategoryId=${
                                  subcat.id
                                }&subcategoryName=${encodeURIComponent(
                                  subcat.name
                                )}`}
                              >
                                {subcat.name}
                              </Link>
                            </li>
                          ))
                        ) : (
                          <li className="text-muted">
                            No subcategories available
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-5">
            <Link
              to="/explore"
              className="btn btn-success btn-lg rounded-pill px-5"
            >
              Explore Services
            </Link>
          </div>
        </div>

        {/* INLINE STYLES */}
        <style>{`
    /* Prevent horizontal scroll */
    .categories-section {
      background: linear-gradient(180deg, #ffffff, #f9fafb);
      overflow-x: hidden;
    }

    .categories-bg-glow {
      position: absolute;
      inset: -200px;
      background: radial-gradient(circle at top, rgba(34,197,94,.15), transparent 60%);
      pointer-events: none;
    }

    /* Search Pill */
    .search-pill {
      display: flex;
      border-radius: 999px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0,0,0,.08);
    }

    .search-pill .form-control {
      border: none;
      border-radius: 999px 0 0 999px;
      padding: 12px 20px;
      box-shadow: none;
    }

    .search-pill .btn {
      border: none;
      border-radius: 0 999px 999px 0;
      background: #fff;
      color: #16a34a;
      transition: background .2s, color .2s;
    }

    .search-pill .btn:hover {
      background: #16a34a;
      color: #fff;
    }

    .category-card {
      background: #fff;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 20px 40px rgba(0,0,0,.08);
      transition: transform .3s, box-shadow .3s;
      height: 100%;
    }

    .category-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 30px 60px rgba(0,0,0,.12);
    }

    .category-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 20px;
    }

    .category-icon {
      width: 48px;
      height: 48px;
      border-radius: 14px;
      background: rgba(34,197,94,.15);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #16a34a;
      font-size: 22px;
      flex-shrink: 0;
    }

    .categories-scroll-wrapper {
      max-height: 560px;
      overflow-y: auto;
      overflow-x: hidden;
      padding-right: 8px;
    }

    /* Slim scrollbar (WebKit) */
    .categories-scroll-wrapper::-webkit-scrollbar {
      width: 6px;
    }

    .categories-scroll-wrapper::-webkit-scrollbar-track {
      background: transparent;
    }

    .categories-scroll-wrapper::-webkit-scrollbar-thumb {
      background: rgba(34, 197, 94, 0.4);
      border-radius: 10px;
    }

    .categories-scroll-wrapper::-webkit-scrollbar-thumb:hover {
      background: rgba(34, 197, 94, 0.6);
    }

    /* Firefox */
    .categories-scroll-wrapper {
      scrollbar-width: thin;
      scrollbar-color: rgba(34,197,94,.5) transparent;
    }

    .subcategory-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .subcategory-list li {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      font-weight: 500;
    }

    .subcategory-list a {
      color: #111;
      text-decoration: none;
      transition: color .2s;
    }

    .subcategory-list a:hover {
      color: #16a34a;
    }

    .spin {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}</style>
      </section>

      {/* Business Services */}
      <BusinessSection />

      {/* Pricing */}
      <section className="text-center py-5 bg1" id="pricing">
        <div className="container">
          <h1 className="fw700 mb-3">Simple, Transparent Pricing</h1>
          <p className="text-muted fs17 fw600 mb-4">
            Choose the plan that works for your scheduling needs. Try any plan
            free for 30 days!
          </p>
          <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
            <span className={currency === "INR" ? "fw700" : "text-muted"}>
              INR (₹)
            </span>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="currencySwitch"
                checked={currency === "USD"}
                onChange={() => setCurrency(currency === "INR" ? "USD" : "INR")}
              />
            </div>

            <span className={currency === "USD" ? "fw700" : "text-muted"}>
              USD ($)
            </span>
          </div>

          <div className="row justify-content-center g-4 mb-4">
            <PricingCard
              title="Basic"
              price={priceMap[currency].basic}
              currency={currency}
              period="/year"
              idealFor="Small local businesses (single branch)"
              features={[
                "Company Registration",
                "Branch Management (Up to 2 branches)",
                "Service Management",
                "Employee Management (Up to 5 employees)",
                "Basic Customer Appointment Management (create/view appointments)",
                "Booking Controls (Accept / Cancel only)",
                "Customer Side Booking",
                "Customer Profiles & Address Management",
                "Basic Customer Feedback",
                "Basic Public Profile Page",
                "Email support",
              ]}
            />
            <PricingCard
              title="Professional"
              price={priceMap[currency].professional}
              currency={currency}
              period="/year"
              idealFor="Growing businesses with multiple branches"
              popular
              features={[
                "Company Registration",
                "Branch Management (Up to 5 branches)",
                "Service Management",
                "Employee Management (Up to 20 employees)",
                "Advanced Customer Appointment Management (daily/weekly reports)",
                "Booking Controls (Accept / Cancel / Reschedule)",
                "Manual Billing + Receipts",
                "Customer Side Booking",
                "Customer Profiles & Address Management",
                "Ratings + Comment Moderation",
                "Custom URL + Photo Gallery",
                "Basic Booking Stats",
                "Priority Email + Chat Support",
                "Future-ready: Payment Gateway Integration",
              ]}
            />
            <PricingCard
              title="Enterprise"
              price={priceMap[currency].enterprise}
              currency={currency}
              period="/year"
              idealFor="Large chains / multi-city service networks"
              features={[
                "Company Registration",
                "Unlimited Branch Management",
                "Advanced Service Management (categorization & bulk upload)",
                "Unlimited Employee Management + Roles & Permissions",
                "Full Customer Appointment Management (analytics, export reports, trends)",
                "Full Booking Controls + Automated Confirmations & Reminders",
                "Full Billing with Tax, Downloadable Invoices",
                "Customer Side Booking",
                "Customer Profiles & Address Management",
                "Ratings Dashboard + Insights",
                "SEO-Optimized Profile + Featured Placement",
                "Detailed Branch & Employee Analytics",
                "Dedicated Manager Support",
                "Future-ready: API Access, WhatsApp Notifications, Loyalty System",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="text-center py-5 ctabg">
        <div className="container">
          <h2 className="fw-bold">Ready to simplify your scheduling?</h2>
          <p className="text-muted mb-3 parabx">
            Join thousands saving time and improving client experience.
          </p>
          <a href="#" className="btn btn-dark mb-4">
            Get Started Free
          </a>
          <p className="mt-2 text-muted small">
            No credit card required. 30-day free trial on all plans.
          </p>
        </div>
      </section>

      {/* Footer */}
      <Footer publicUrl={publicUrl} />
      <style jsx>{`
        .hover-card {
          transition: all 0.2s ease-in-out;
        }
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
        .subcategory-link:hover {
          color: #28a745 !important;
        }
        .subcategory-link:hover i {
          transform: translateX(2px);
          transition: transform 0.2s ease-in-out;
        }
      `}</style>
    </>
  );
};

const PricingCard = ({
  title,
  price,
  currency,
  period,
  idealFor,
  features,
  popular,
}) => {
  const currencySymbol = currency === "USD" ? "$" : "₹";

  return (
    <div className="col-md-4 col-lg-4 position-relative">
      {popular && <div className="most-popular-badge">MOST POPULAR</div>}
      <div
        className={`card pricing-card ${
          popular ? "active border-info" : ""
        } p-4`}
      >
        <div className="card-body">
          <h5 className="card-title fw600 text-left">{title}</h5>
          <h2 className="fw700 text-left mb-2">
            {currencySymbol}
            {price}
            <small className="text-muted">{period}</small>
          </h2>
          <p className="text-muted text-left mb-3">{idealFor}</p>
          <ul className="list-unstyled my-3 text-start">
            {features.map((feat, idx) => (
              <li key={idx} className="mb-2">
                ✔️ {feat}
              </li>
            ))}
          </ul>
          <a
            href="#"
            className={`btn w-100 ${
              popular ? "btn-success text-white" : "btn-outline-success"
            }`}
          >
            Try Free for 30 Days
          </a>
        </div>
      </div>
    </div>
  );
};

const Footer = ({ publicUrl }) => (
  <footer className="pt-5 pb-1 customftr bg-dark text-light">
    <div className="container">
      <div className="row gy-4">
        <div className="col-md-3">
          <h5>
            <img
              src={`${publicUrl}/logo2.png`}
              alt="Logo"
              style={{ maxWidth: 150, filter: "grayscale(100%)" }}
            />
          </h5>
          <p>Scheduling made simple.</p>
          <div className="d-flex gap-4">
            {["twitter-x", "linkedin", "instagram", "facebook"].map(
              (icon, idx) => (
                <a href="#" key={idx}>
                  <i className={`bi bi-${icon}`} />
                </a>
              )
            )}
          </div>
        </div>
        <FooterBlock
          title="Company"
          items={["About Us", "Privacy Policy", "Terms of Service"]}
        />
        <FooterBlock
          title="Product"
          items={["Features", "Pricing", "Integrations"]}
        />
        <FooterBlock title="Resources" items={["Blog", "Guides", "API Docs"]} />
      </div>
      <div
        className="text-center mt-4"
        style={{ borderTop: "1px solid #383a3b", paddingTop: 30 }}
      >
        <p className="mb-1">© 2025 TickTickGo.in | All rights reserved.</p>
        <p>Made with ❤️ in India</p>
      </div>
    </div>
  </footer>
);

const FooterBlock = ({ title, items }) => (
  <div className="col-md-3">
    <h5>{title}</h5>
    <ul className="list-unstyled">
      {items.map((text, idx) => (
        <li key={idx}>
          <a href="#">{text}</a>
        </li>
      ))}
    </ul>
  </div>
);

export default HomePage;
